import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOMeta from '@/components/base/SEOMeta';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { getPostBySlug, getFeaturedPosts, getPostsByCategory } from '@/mocks/posts';
import { categories } from '@/mocks/posts';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getPostBySlug(slug) : undefined;

  if (!post || !slug) {
    return (
      <>
        <ScrollToTop />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-20 pb-14">
            <div className="max-w-4xl mx-auto px-4 md:px-6 text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
                <i className="ri-file-unknow-line text-2xl text-foreground-400 w-8 h-8 flex items-center justify-center"></i>
              </div>
              <h2 className="text-xl font-heading font-bold text-foreground-950 mb-2">文章未找到</h2>
              <p className="text-foreground-600 mb-6">抱歉，你访问的文章不存在或已被删除</p>
              <Link
                to="/"
                className="inline-flex px-6 py-2 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                返回首页
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const relatedPosts = getPostsByCategory(post.categorySlug)
    .filter((p) => p.slug !== post.slug)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.excerpt,
    author: {
      '@type': 'Person',
      name: post.author,
    },
    datePublished: post.publishDate,
    image: post.coverImage,
    publisher: {
      '@type': 'Organization',
      name: '海角社区',
    },
  };

  return (
    <>
      <ScrollToTop />
      <SEOMeta
        title={post.title}
        description={post.excerpt}
        keywords={post.tags.join(',')}
        canonicalUrl={`https://daipumall.cn/article/${post.slug}`}
        ogType="article"
      />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-14">
          <article className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: post.category, href: `/category/${post.categorySlug}` },
                { label: post.title },
              ]}
            />

            {/* Cover */}
            <div className="aspect-[21/9] rounded-xl overflow-hidden mb-8">
              <img
                src={post.coverImage}
                alt={post.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* Header */}
            <header className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <Link
                  to={`/category/${post.categorySlug}`}
                  className="text-xs px-3 py-1 rounded-full bg-primary-100 text-primary-700 font-medium hover:bg-primary-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {post.category}
                </Link>
                <span className="text-sm text-foreground-400">{post.publishDate}</span>
                <span className="text-sm text-foreground-400">{post.readTime} 分钟阅读</span>
              </div>
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 leading-tight mb-4">
                {post.title}
              </h1>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full overflow-hidden bg-background-200 flex-shrink-0">
                  <img
                    src={post.authorAvatar}
                    alt={post.author}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-800">{post.author}</p>
                </div>
              </div>
            </header>

            {/* Content */}
            <div
              className="prose prose-lg max-w-none mb-12 text-foreground-800 leading-relaxed
                prose-headings:font-heading prose-headings:text-foreground-950
                prose-p:text-foreground-700 prose-p:leading-relaxed
                prose-strong:text-foreground-900
                prose-a:text-primary-500 prose-a:no-underline hover:prose-a:text-primary-600"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />

            {/* Tags */}
            <div className="flex flex-wrap items-center gap-2 mb-10 pt-6 border-t border-background-200">
              {post.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-800 text-xs font-medium"
                >
                  #{tag}
                </span>
              ))}
            </div>

            {/* Likes & Comments */}
            <div className="flex items-center gap-6 mb-12 pb-8 border-b border-background-200">
              <button className="flex items-center gap-2 text-foreground-600 hover:text-accent-500 transition-colors cursor-pointer whitespace-nowrap">
                <div className="w-8 h-8 rounded-full bg-background-200 flex items-center justify-center">
                  <i className="ri-heart-line w-4 h-4 flex items-center justify-center"></i>
                </div>
                <span className="text-sm">{post.likes.toLocaleString()}</span>
              </button>
              <button className="flex items-center gap-2 text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">
                <div className="w-8 h-8 rounded-full bg-background-200 flex items-center justify-center">
                  <i className="ri-chat-1-line w-4 h-4 flex items-center justify-center"></i>
                </div>
                <span className="text-sm">{post.comments.toLocaleString()}</span>
              </button>
              <button className="flex items-center gap-2 text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap ml-auto">
                <div className="w-8 h-8 rounded-full bg-background-200 flex items-center justify-center">
                  <i className="ri-share-forward-line w-4 h-4 flex items-center justify-center"></i>
                </div>
              </button>
            </div>

            {/* Related */}
            {relatedPosts.length > 0 && (
              <section>
                <h3 className="text-lg font-heading font-bold text-foreground-950 mb-5">相关文章</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {relatedPosts.map((rp) => (
                    <Link
                      key={rp.id}
                      to={`/article/${rp.slug}`}
                      className="group bg-background-100 rounded-lg overflow-hidden hover:bg-background-200/70 transition-all cursor-pointer"
                    >
                      <div className="aspect-[16/10] overflow-hidden">
                        <img
                          src={rp.coverImage}
                          alt={rp.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-3">
                        <h4 className="text-sm font-medium text-foreground-800 group-hover:text-primary-500 transition-colors line-clamp-2">
                          {rp.title}
                        </h4>
                        <p className="text-xs text-foreground-400 mt-1">{rp.publishDate}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </article>
        </main>
        <Footer />
      </div>
    </>
  );
}